import { Box, Grid, Typography } from '@mui/material';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { makeSemiProduct } from '../../api/store/makeSemiProduct';
import EditModal from '../../components/EditModal';
import FormAutocomplete from '../../components/forms/FormAutocomplete';
import FormSelect from '../../components/forms/FormSelect';
import Image from '../../components/Image';
import QntInput from '../../components/QntInput';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import getQntLabel from '../../utils/getQntLabel';
import EditStore from '../Stores/EditStore';

type Props = {
  onClose: () => void;
};

type FormType = {
  semiProductId?: string;
  quantity: number;
  storeId?: string;
};

const PrepareModal = ({ onClose }: Props) => {
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { semiProducts, semiProductsMap, ingredientsMap, storesMap, stores, worker } = useCafe();
  const [openAddStore, setOpenAddStore] = useState(false);

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      quantity: 0,
    },
  });

  const semiProductId = form.watch('semiProductId');
  const quantity = form.watch('quantity');
  const semiProduct = semiProductId ? semiProductsMap[semiProductId] : undefined;

  const handleClose = () => {
    onClose();
  };

  const saveChanges = useCallback(
    (values: FormType) => {
      const { semiProductId, storeId, quantity } = values;
      const semiProduct = semiProductId ? semiProductsMap[semiProductId] : undefined;

      if (semiProduct && storeId && worker) {
        makeSemiProduct({ semiProduct, quantity: Math.round(quantity), storeId, cafeSlug, worker });
        onClose();
      }
    },
    [cafeSlug, onClose, semiProductsMap, worker]
  );

  useEffect(() => {
    if (semiProductId) {
      const store = stores.find((s) => s.residues[semiProductId]) || stores[0];
      form.setValue('storeId', store?.id);
    }
  }, [form, semiProductId, stores]);

  return (
    <>
      <EditModal<FormType>
        title={t.prepare_semifinished_product[lang]}
        open
        onClose={handleClose}
        submitButton={t.make_semifinished_product[lang]}
        onSubmit={saveChanges}
        submitButtonProps={{ disabled: !quantity || !semiProduct }}
        form={form}
        size="sm"
      >
        <>
          <Box maxWidth={300} m="0 auto">
            <FormAutocomplete
              name="semiProductId"
              required
              fullWidth
              label={t.semifinished_product[lang]}
              error={!!form.formState.errors.semiProductId}
              options={semiProducts.map((s) => s.id)}
              renderOption={(props, o) => (
                <Box component="li" {...props}>
                  <Image src={semiProductsMap[o]?.imageUrl} />{' '}
                  <Typography sx={{ ml: 1 }}>{semiProductsMap[o]?.name}</Typography>
                </Box>
              )}
              getOptionLabel={(o) => semiProductsMap[o]?.name || ''}
              rules={{ required: true }}
              sx={{ mb: 1 }}
            />

            <Box height={120} sx={{ mb: 4 }}>
              {semiProduct && (
                <>
                  <Typography sx={{ color: (t) => t.palette.text.secondary, fontSize: 12, ml: 1 }}>
                    {t.quantity[lang]}
                  </Typography>
                  <QntInput
                    ingredient={semiProduct}
                    qnt={Math.round(quantity) || 0}
                    autoFocus
                    onChange={(qnt: number) => {
                      form.setValue('quantity', qnt);
                    }}
                  />

                  <FormSelect
                    name="storeId"
                    label={t.store[lang]}
                    options={stores.map((s) => s.id)}
                    size="small"
                    renderOption={(o) => storesMap[o]?.name}
                    sx={{ mt: 3 }}
                  />
                </>
              )}
            </Box>
          </Box>

          {semiProduct && (
            <Grid container spacing={1} p={1}>
              <Grid item xs={4}>
                <Typography variant="body2" color={(t) => t.palette.text.secondary}>
                  {t.component[lang]}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color={(t) => t.palette.text.secondary}>
                  {t.costs_of_production[lang]}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Typography align="right" variant="body2" color={(t) => t.palette.text.secondary}>
                  {t.total_in_stock[lang]}
                </Typography>
              </Grid>

              {semiProduct?.components.map((c) => {
                const ingredient = ingredientsMap[c.id];
                if (!ingredient) {
                  return null;
                }

                const store = c.store && storesMap[c.store];
                const totalQnt = store ? store.residues[c.id] || 0 : 0;
                const qnt = Math.round((c.qnt / (semiProduct.batchVolume || 1)) * quantity);
                const warning = totalQnt < qnt;

                return (
                  <Fragment key={c.id}>
                    <Grid item xs={4} sx={{ mt: 1 }}>
                      <Typography>{ingredient.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <QntInput
                        ingredient={ingredient}
                        qnt={qnt}
                        onChange={(value) =>
                          form.setValue('quantity', (value * (semiProduct.batchVolume || 1)) / c.qnt)
                        }
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ mt: 1 }}>
                      <Typography align="right" color={(t) => (warning ? t.palette.error.main : undefined)}>
                        {getQntLabel(totalQnt, ingredient, lang)}
                      </Typography>
                    </Grid>
                  </Fragment>
                );
              })}
            </Grid>
          )}
        </>
      </EditModal>

      {openAddStore && <EditStore open onClose={() => setOpenAddStore(false)} />}
    </>
  );
};

export default PrepareModal;
